<template>
  <div class="user-sidebar">
    <div class="sidebar-header">
      <h3>{{ title || '用户列表' }}</h3>
      <div class="user-count">
        <span v-if="!multiSelect">{{ displayUsers.length }} 个用户</span>
        <span v-else>{{ selectedUsers.length }}/{{ displayUsers.length }} 已选择</span>
      </div>
    </div>
    <div class="user-list" v-loading="isComponentLoading">
      <div v-if="!isComponentLoading && displayUsers.length === 0" class="empty-state">
        <div class="empty-icon">👥</div>
        <div class="empty-text">{{ error || '暂无用户数据' }}</div>
      </div>
      <div v-for="user in displayUsers" :key="user.id" :class="[
        'user-item',
        { active: !multiSelect && selectedUser && selectedUser.id === user.id },
        { 'multi-selected': multiSelect && isUserSelected(user) }
      ]" @click="handleUserClick(user)">

        <div class="user-avatar">
          <img :src="user.personalAvatar_xl ||
            user.avatar ||
            require('@/assets/profile.png')
            " :alt="user.name || user.nickname" />
          <!-- <div class="status-indicator" :class="{ online: user.onLine }"></div> -->
        </div>
        <div class="user-info">
          <div class="user-nickname">{{ user.name || user.nickname }}</div>
          <div class="user-id">ID: {{ user.id }}</div>
          <!-- 帖子时间状态指示器 - 仅在newsFeed页面显示 -->
          <div v-if="showPostTimeStatus && (getFormattedSendTime(user) || getUserPostTimeStatusText(user))" class="post-time-status">
            <div :class="['post-time-badge', getUserPostStatusClass(user)]">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
              </svg>
              <div class="post-time-text">
                <span v-if="getFormattedSendTime(user)" class="post-time-send-time">
                  {{ getFormattedSendTime(user) }}
                </span>
                <span v-if="getUserPostTimeStatusText(user)" class="post-time-extra">
                  {{ getUserPostTimeStatusText(user) }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="user-actions" v-if="showUnreadCount && user.unreadCount > 0">
          <div class="unread-badge">
            <span class="unread-count">
              {{ user.unreadCount > 99 ? '99+' : user.unreadCount }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import dayjs from 'dayjs';
import { getUserLastPostTimes } from '@/api/talkytimes/newsFeed';

export default {
  name: 'UserListSidebar',
  props: {
    selectedUser: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: '用户列表',
    },
    // 保留users prop以支持向后兼容
    users: {
      type: Array,
      default: () => [],
    },
    // 保留loading prop以支持向后兼容
    loading: {
      type: Boolean,
      default: false,
    },
    // 是否显示未读徽章
    showUnreadCount: {
      type: Boolean,
      default: false,
    },
    // 是否启用多选模式
    multiSelect: {
      type: Boolean,
      default: false,
    },
    // 多选模式下已选择的用户ID数组
    selectedUserIds: {
      type: Array,
      default: () => [],
    },
    showBatchOperation: {
      type: Boolean,
      default: false,
    },
    // 是否显示帖子时间状态
    showPostTimeStatus: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      userSendInfoMap: {},
    };
  },
  computed: {
    ...mapGetters('accountList', ['getAccountList', 'isLoading', 'getError']),
    displayUsers() {
      // 使用getter确保响应式
      const storeData = this.getAccountList || [];
      // console.log(
      //   'UserListSidebar - Store data:',
      //   storeData,
      //   'Props users:',
      //   this.users
      // );
      // console.log('UserListSidebar - selectedUser:', this.selectedUser);
      return storeData.length > 0 ? storeData : this.users;
    },
    isComponentLoading() {
      // 从store或props获取loading状态
      return this.isLoading || this.loading;
    },
    error() {
      return this.getError;
    },
    // 多选模式下已选择的用户
    selectedUsers() {
      if (!this.multiSelect) return [];
      return this.displayUsers.filter(user => this.selectedUserIds.includes(user.id));
    },
  },
  async mounted() {
    // console.log('UserListSidebar mounted');
    // console.log('Store state:', this.$store.state.accountList);
    // console.log('Props users:', this.users);

    // 如果store和props都没有数据，则获取数据
    const storeData = this.$store.state.accountList?.accountList || [];
    if (storeData.length === 0 && this.users.length === 0) {
      // console.log('Fetching account list...');
      try {
        await this.fetchAccountList();
      } catch (error) {
        console.error('Error fetching account list:', error);
      }
    }

    await this.fetchUserSendInfo();
  },
  methods: {
    ...mapActions('accountList', ['fetchAccountList']),
    selectUser(user) {
      // console.log('UserListSidebar selectUser called with:', user);
      localStorage.setItem('activeUser', JSON.stringify(user));
      this.$emit('select-user', user);
    },
    // 处理用户点击事件
    handleUserClick(user) {
      if (this.multiSelect) {
        // 多选模式下，点击用户项切换选择状态
        this.toggleUserSelection(user);
      } else {
        // 单选模式下，选择用户
        this.selectUser(user);
      }
    },
    // 切换用户选择状态
    toggleUserSelection(user) {
      if (!this.multiSelect) return;

      const userId = user.id;
      const newSelectedIds = [...this.selectedUserIds];
      const index = newSelectedIds.indexOf(userId);

      if (index > -1) {
        // 取消选择
        newSelectedIds.splice(index, 1);
      } else {
        // 添加选择
        newSelectedIds.push(userId);
      }

      // 触发事件通知父组件
      this.$emit('update:selectedUserIds', newSelectedIds);
      this.$emit('selection-change', {
        selectedIds: newSelectedIds,
        selectedUsers: this.displayUsers.filter(u => newSelectedIds.includes(u.id)),
        action: index > -1 ? 'deselect' : 'select',
        user: user
      });
    },
    // 检查用户是否被选中
    isUserSelected(user) {
      return this.selectedUserIds.includes(user.id);
    },
    async fetchUserSendInfo() {
      if (!this.showPostTimeStatus) {
        return;
      }
      try {
        const response = await getUserLastPostTimes();
        if (response && response.code === 200 && Array.isArray(response.data)) {
          const map = {};
          response.data.forEach(item => {
            if (item && item.pdUserId) {
              const key = String(item.pdUserId);
              map[key] = item;
            }
          });
          this.userSendInfoMap = map;
        }
      } catch (error) {
        console.error('获取用户发送时间失败:', error);
      }
    },
    getUserSendInfo(user) {
      if (!user || !user.id) return null;
      const key = String(user.id);
      return this.userSendInfoMap[key] || this.userSendInfoMap[user.id] || user;
    },
    getUserSendTime(user) {
      const info = this.getUserSendInfo(user);
      return info?.sendTime || info?.lastPostTime || null;
    },
    formatSendTime(sendTime) {
      if (!sendTime) return '';
      const parsed = dayjs(sendTime);
      return parsed.isValid() ? parsed.format('YYYY/MM/DD HH:mm') : sendTime;
    },
    getFormattedSendTime(user) {
      const sendTime = this.getUserSendTime(user);
      if (!sendTime) return '';
      return this.formatSendTime(sendTime);
    },
    getUserPostStatusClass(user) {
      const info = this.getUserSendInfo(user);
      const sendTime = info?.sendTime || info?.lastPostTime;

      if (sendTime && dayjs(sendTime).isValid()) {
        const hoursDiff = dayjs().diff(dayjs(sendTime), 'hour');
        if (!Number.isNaN(hoursDiff) && hoursDiff >= 24) {
          return 'post-old';
        }
      }

      if (info && info.isNowSend === false) {
        return 'post-old';
      }

      return info?.postTimeStatus || 'normal';
    },
    // 获取用户帖子时间状态文本
    getUserPostTimeStatusText(user) {
      const info = this.getUserSendInfo(user);
      const sendTime = info?.sendTime || info?.lastPostTime;

      if (sendTime && dayjs(sendTime).isValid()) {
        const hoursDiff = dayjs().diff(dayjs(sendTime), 'hour');
        if (!Number.isNaN(hoursDiff) && hoursDiff >= 24) {
          return `超过${hoursDiff}小时未发帖`;
        }
        return '';
      }

      if (info && info.isNowSend === true) {
        return '已发帖';
      }

      return '';
    },
  },
  watch: {
    showPostTimeStatus(newVal, oldVal) {
      if (newVal && !oldVal && Object.keys(this.userSendInfoMap).length === 0) {
        this.fetchUserSendInfo();
      }
    },
  },
};
</script>

<style scoped lang="scss">
/* 左侧用户选择 */
.user-sidebar {
  width: 300px;
  height: calc(100vh - 64px);
  /* 铺满整个视口高度 */
  background: rgba(255, 255, 255, 0.8);
  border-right: 1px solid rgba(229, 231, 235, 0.8);
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 20px;
  border-bottom: 1px solid rgba(229, 231, 235, 0.8);
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.sidebar-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.user-count {
  font-size: 13px;
  color: #6b7280;
  background: rgba(139, 92, 246, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 500;
}

.user-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.user-item {
  display: flex;
  align-items: center;
  padding: 16px;
  margin-bottom: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
  background: rgba(241, 245, 249, 0.8);
  position: relative;
  overflow: hidden;
}

.user-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent);
  transition: left 0.5s;
}

.user-item:hover::before {
  left: 100%;
}

.user-item:hover {
  background: rgba(226, 232, 240, 0.9);
  border-color: rgba(139, 92, 246, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(139, 92, 246, 0.15);
}

.user-item.active {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border-color: #8b5cf6;
  box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3);
  transform: translateY(-2px);
}

.user-item.active .user-nickname {
  color: white;
}

.user-item.active .user-id {
  color: rgba(255, 255, 255, 0.8);
}

/* 多选模式样式 - 与单选样式保持一致 */
.user-item.multi-selected {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border-color: #8b5cf6;
  box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3);
  transform: translateY(-2px);
}

.user-item.multi-selected .user-nickname {
  color: white;
}

.user-item.multi-selected .user-id {
  color: rgba(255, 255, 255, 0.8);
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 16px;
  border: 3px solid rgba(229, 231, 235, 0.8);
  position: relative;
  transition: all 0.3s ease;
}

.user-item.active .user-avatar {
  border-color: rgba(255, 255, 255, 0.8);
  transform: scale(1.1);
}

.user-item.multi-selected .user-avatar {
  border-color: rgba(255, 255, 255, 0.8);
  transform: scale(1.1);
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.user-item:hover .user-avatar img {
  transform: scale(1.1);
}

.status-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  background: #9ca3af;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.status-indicator.online {
  background: #10b981;
  animation: pulse 2s infinite;
}

.user-info {
  flex: 1;
}

.user-nickname {
  font-weight: 700;
  font-size: 15px;
  color: #1f2937;
  margin-bottom: 4px;
  transition: color 0.3s ease;
}

.user-id {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  transition: color 0.3s ease;
}

/* 帖子时间状态样式 */
.post-time-status {
  margin-top: 4px;
}

.post-time-badge {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  width: fit-content;
}

.post-time-badge svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.post-time-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.4;
}

.post-time-send-time {
  font-size: 11px;
}

.post-time-extra {
  font-size: 10px;
}

.post-time-badge.normal {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1d4ed8;
  box-shadow: 0 1px 3px rgba(29, 78, 216, 0.1);
}

.post-time-badge.warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
  box-shadow: 0 1px 3px rgba(217, 119, 6, 0.15);
}

.post-time-badge.old {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  box-shadow: 0 1px 3px rgba(220, 38, 38, 0.15);
}

.post-time-badge.post-old {
  background: #ffe9e3;
  color: #e04b23;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  box-shadow: 0 2px 8px rgba(224, 75, 35, 0.09);
}
.post-time-badge.post-old svg {
  color: #e04b23;
}


.user-actions {
  display: flex;
  align-items: center;
  margin-left: 15px;
  opacity: 1;
  transition: opacity 0.3s ease;
}

.unread-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 10px;
  padding: 0 6px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
  animation: badgePulse 2s infinite;
  border: 2px solid rgba(255, 255, 255, 0.9);
}

.unread-count {
  color: white;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  user-select: none;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #9ca3af;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-text {
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

/* 滚动条样式 */
.user-list::-webkit-scrollbar {
  width: 8px;
}

.user-list::-webkit-scrollbar-track {
  background: rgba(241, 245, 249, 0.8);
  border-radius: 4px;
}

.user-list::-webkit-scrollbar-thumb {
  background: rgba(139, 92, 246, 0.3);
  border-radius: 4px;
  transition: background 0.3s ease;
}

.user-list::-webkit-scrollbar-thumb:hover {
  background: rgba(139, 92, 246, 0.5);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes badgePulse {
  0% {
    transform: scale(1);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
  }

  50% {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.6);
  }

  100% {
    transform: scale(1);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .user-sidebar {
    width: 250px;
  }
}

@media (max-width: 480px) {
  .user-sidebar {
    width: 200px;
  }

  .sidebar-header {
    padding: 16px;
  }

  .user-item {
    padding: 12px;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    margin-right: 12px;
  }


}
</style>
